// Reusable atoms for report sections
export const SectionTitle = ({ id, index, title, sub }) => (
  <div className="mb-5 pb-3 border-b border-[#262626]">
    <div className="flex items-baseline gap-3 mb-1">
      <span className="kpi-label text-[#525252]">/{String(index).padStart(2, "0")}</span>
      <h2 id={id} className="font-display text-xl md:text-2xl tracking-tight text-white">{title}</h2>
    </div>
    {sub && <p className="text-sm text-[#A3A3A3]">{sub}</p>}
  </div>
);

export const Card = ({ children, className = "" }) => (
  <div className={`bg-[#141414] border border-[#262626] rounded-sm ${className}`}>{children}</div>
);

export const KV = ({ label, value, mono = false }) => (
  <div className="flex flex-col gap-1 py-2.5 border-b border-[#1F1F1F] last:border-b-0">
    <span className="kpi-label">{label}</span>
    <span className={`text-sm text-white ${mono ? "font-mono-data" : ""}`}>{value || <span className="text-[#525252]">—</span>}</span>
  </div>
);

export const Pill = ({ children, tone = "neutral", ...rest }) => {
  const tones = {
    neutral: "border-[#262626] bg-[#1F1F1F] text-[#A3A3A3]",
    blue: "border-[#0A84FF]/40 bg-[#0A84FF]/10 text-[#0A84FF]",
    green: "border-[#32D74B]/40 bg-[#32D74B]/10 text-[#32D74B]",
    amber: "border-[#FF9F0A]/40 bg-[#FF9F0A]/10 text-[#FF9F0A]",
    red: "border-[#FF453A]/40 bg-[#FF453A]/10 text-[#FF453A]",
  };
  return (
    <span {...rest} className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const BulletList = ({ items, testId }) => {
  if (!items || items.length === 0) return <span className="text-[#525252] text-sm">—</span>;
  return (
    <ul className="space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm text-[#E5E5E5] leading-relaxed">
          <span className="kpi-label text-[#525252] shrink-0 mt-0.5 w-6">{String(i + 1).padStart(2, "0")}</span>
          <span>{typeof it === "string" ? it : JSON.stringify(it)}</span>
        </li>
      ))}
    </ul>
  );
};

export const DataTable = ({ columns, rows, testId }) => {
  if (!rows || rows.length === 0) return <div className="text-[#525252] text-sm">No data</div>;
  return (
    <div className="border border-[#262626] rounded-sm overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-[#1F1F1F] sticky top-0">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`text-left px-3 py-2.5 kpi-label whitespace-nowrap ${c.align === "right" ? "text-right" : ""}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 ? "bg-[#0A0A0A]" : "bg-[#141414]"}>
              {columns.map((c) => {
                const v = row[c.key];
                return (
                  <td
                    key={c.key}
                    className={`px-3 py-2.5 align-top text-[#E5E5E5] ${c.mono ? "font-mono-data" : ""} ${c.align === "right" ? "text-right" : ""}`}
                  >
                    {v == null || v === "" ? <span className="text-[#525252]">—</span> : (typeof v === "string" || typeof v === "number") ? v : JSON.stringify(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const Subgrid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

export const SubCard = ({ title, children }) => (
  <Card className="p-5">
    <div className="kpi-label mb-3">{title}</div>
    {children}
  </Card>
);
